import React, { FC } from "react";
import { FormHelperText, Grid, InputLabel, Tooltip } from "@mui/material";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormFieldProps } from "../../../utils/formFields";
import useStyles from "../../../components/common/model/styles";
import dayjs from "../../../utils/dayjsConfig";
import type { Dayjs } from "dayjs"; // ✅ type import
import { useSelector } from "react-redux";

const FormDateField: FC<FormFieldProps> = (props) => {
  const classes = useStyles();
  const { userProfile } = useSelector((state: any) => state?.user);
  const { editClientOriginalData } = useSelector(
    (state: any) => state.adminClients
  );

  const clientTimeZone = editClientOriginalData?.timezone || "UTC";

  const handleDateChange = (date: Dayjs | null) => {
    const event = {
      target: {
        id: props.id,
        value: null as string | null,
      },
    };

    try {
      if (props.type === "date-time") {
        if (date) {
          // ✅ Convert client time → UTC ISO for backend
          const clientDate = date.tz(clientTimeZone, true); // keep local time
          event.target.value = clientDate.utc().toISOString();
        } else {
          event.target.value = null;
        }
      } else {
        // Date-only picker
        event.target.value = date ? date.format("YYYY-MM-DD") : null;
      }
    } catch (error) {
      event.target.value = null;
    }

    props?.onChange?.(event);
  };

  const shouldDisableTime = (timeValue: number, clockType: "hours" | "minutes") => {
    if (!props.value) return false;

    try {
      const selectedDate = dayjs(props.value).tz(clientTimeZone);
      const currentDate = dayjs().tz(clientTimeZone);
      if (selectedDate.isBefore(currentDate, "day")) {
        if (clockType === "hours") return true;
      }
    } catch {
      return false;
    }

    return false;
  };

  const inputRender = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {props.type === "date-time" ? (
        <DateTimePicker
          sx={{
            width: "100%",
            background: "#fff",
            "&.MuiFormControl-root": { borderRadius: "5px" },
          }}
          disablePast={props?.disablePast || false}
          disabled={props?.disabled}
          ampm={false} // 24-hour format
          minDate={props.minDate ? dayjs(props.minDate).tz(clientTimeZone) : undefined}
          minDateTime={props.minDate ? dayjs(props.minDate).tz(clientTimeZone) : undefined}
          value={props?.value ? dayjs.utc(props.value).tz(clientTimeZone) : null}
          format="DD/MM/YYYY HH:mm"
          onChange={handleDateChange}
          // shouldDisableTime={shouldDisableTime}
          slotProps={{
            textField: {
              id: props.id,
              name: props.id,
              size: "small",
              error: !!props.error,
              onBlur: () => props?.onBlur?.({ target: { id: props.id } }),
              InputProps: { sx: { height: "36px" } },
            },
          }}
        />
      ) : (
        <DatePicker
          sx={{
            width: "100%",
            background: "#fff",
            "&.MuiFormControl-root": { borderRadius: "5px" },
          }}
          disablePast={props.disablePast || false}
          disabled={props?.disabled}
          minDate={props.minDate ? dayjs(props.minDate).tz(clientTimeZone) : undefined}
          maxDate={props.maxDate ? dayjs(props.maxDate).tz(clientTimeZone) : undefined}
          value={props?.value ? dayjs(props.value) : null}
          format={userProfile?.dateFormat || "DD/MM/YYYY"}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              id: props.id,
              name: props.id,
              size: "small",
              placeholder: userProfile?.dateFormat || "DD/MM/YYYY",
              onBlur: () => props?.onBlur?.({ target: { id: props.id } }),
              InputProps: {
                sx: { height: "36px", border: props.error ? "1px solid #d32f2f" : undefined },
              },
            },
          }}
        />
      )}

      {props.error && (
        <FormHelperText sx={{ color: "#d32f2f", marginLeft: "14px" }}>
          {props.helperText}
        </FormHelperText>
      )}
    </LocalizationProvider>
  );

  return (
    <>
      {props?.label && !props?.inSingleGridForm ? (
        <>
          <Grid
            item
            xs={12}
            sm={props?.md ? 1.5 : 5}
            className={classes.labelBox}
            sx={{ marginTop: props?.isNonMarginTop ? "0px !important" : "" }}
          >
            <InputLabel
              sx={{ color: "#333333", fontWeight: 600, fontSize: "18px" }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && <span style={{ color: "red", fontWeight: 500 }}>*</span>}
            </InputLabel>
          </Grid>
          <Grid
            item
            xs={12}
            sm={props?.md ? props?.md : 7}
            style={{ padding: "0px", marginTop: props?.isNonMarginTop ? "0px" : "16px", marginBottom: "10px" }}
          >
            {inputRender()}
          </Grid>
        </>
      ) : props?.label && props?.inSingleGridForm ? (
        <Grid
          item
          xs={12}
          sm={props?.md ? props?.md : 12}
          sx={{ display: "block", alignItems: "center", gap: 2, ...props?.GridFieldStyle }}
        >
          <Tooltip title={props?.tooltipText || ""} arrow>
            <InputLabel
              sx={{ color: "#637381", fontWeight: 600, fontSize: "16px", textAlign: "left !important" }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && <span style={{ color: "red", fontWeight: 500 }}>*</span>}
            </InputLabel>
          </Tooltip>
          {inputRender()}
        </Grid>
      ) : (
        inputRender()
      )}
    </>
  );
};

export default FormDateField;